import { Button, Kind } from '@thanx/uikit/button'
import { Modal } from '@thanx/uikit/modal'
import { Text } from '@thanx/uikit/text'
import {
  CredentialSharingAuthority,
  CredentialSharingObligation,
} from 'api/credentialSharingAgreement'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { merchantTheme } from 'theme'

const t = buildTranslate('components.credential_sharing_nag_modal')

// The modal has two mutually-reachable views:
// - 'grant'      Case 1: nag the merchant to let Thanx handle Apple (LPA).
// - 'self_serve' Case 2: the merchant manages Apple themselves (App Store links).
// `authority` picks the default view; the secondary CTA toggles between them.
type View = 'grant' | 'self_serve'

type Props = {
  isOpen: boolean
  authority?: CredentialSharingAuthority
  obligations: CredentialSharingObligation[]
  onDismiss: () => void
  onGrantAuthority: () => void
  onManageOnApple: () => void
  onDeclineAuthority: () => void
  onReconsider: () => void
}

const defaultView = (authority?: CredentialSharingAuthority): View =>
  authority === 'declined' ? 'self_serve' : 'grant'

const formatExpiry = (expiresAt: string | null): string | null => {
  if (!expiresAt) return null
  const date = new Date(expiresAt)
  if (isNaN(date.getTime())) return null
  // Render as a calendar date in UTC — `expires_at` is a date-only value, so
  // without a fixed tz `toLocaleDateString` shifts it a day earlier for users
  // west of UTC (all of the Americas).
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const ObligationItem = ({
  obligation,
  showAppleCta,
  onManageOnApple,
}: {
  obligation: CredentialSharingObligation
  showAppleCta: boolean
  onManageOnApple: () => void
}) => {
  const { type, expires_at } = obligation
  const expiry = formatExpiry(expires_at)
  const description =
    type === 'membership'
      ? expiry
        ? t('obligation.membership.description', { expiry })
        : t('obligation.membership.description_no_date')
      : t('obligation.license_agreement.description')

  return (
    <div className="mb-m">
      <Text.BodyText4 tag="div" color="grey90" bold className="mb-xs">
        {t(`obligation.${type}.title`)}
      </Text.BodyText4>
      <Text.BodyText3 tag="div" color="grey70" className="mb-s">
        {description}
      </Text.BodyText3>
      {showAppleCta && (
        // Link style, not a boxed button: in the self-serve view each obligation
        // gets its own "open App Store Connect" action, and a stack of boxed
        // buttons reads as heavy — a hyperlink is the lighter, clearer affordance.
        <Button kind={Kind.MINIMAL} className="p-0" onClick={onManageOnApple}>
          {t(`obligation.${type}.cta`)}
        </Button>
      )}
    </div>
  )
}

const CredentialSharingNagModal = ({
  isOpen,
  authority,
  obligations,
  onDismiss,
  onGrantAuthority,
  onManageOnApple,
  onDeclineAuthority,
  onReconsider,
}: Props) => {
  const [view, setView] = useState<View>(defaultView(authority))

  // Re-seed the view whenever the modal (re)opens or the authority changes, so
  // a merchant re-opening from the banner starts on the case that matches the
  // current backend state rather than a stale toggle.
  useEffect(() => {
    if (isOpen) setView(defaultView(authority))
  }, [isOpen, authority])

  const isGrant = view === 'grant'
  const copy = isGrant ? 'grant' : 'self_serve'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onDismiss}
      zIndex={merchantTheme.zIndex.modal}
    >
      <div className="p-xl">
        <Text.Header3 tag="div" color="grey90" className="mb-s">
          {t(`${copy}.title`)}
        </Text.Header3>
        <Text.BodyText3 tag="div" color="grey70" className="mb-l">
          <span
            dangerouslySetInnerHTML={{ __html: t(`${copy}.description`) }}
          />
        </Text.BodyText3>

        {obligations.map(obligation => (
          <ObligationItem
            key={obligation.type}
            obligation={obligation}
            showAppleCta={!isGrant}
            onManageOnApple={onManageOnApple}
          />
        ))}

        {/* Footer: two real choices, not three side-by-side buttons.
            - Grant: the two decisions are buttons ("manage myself" vs. "let
              Thanx handle it"); "Remind me later" is a text link on the left.
            - Self-serve: the per-obligation App Store Connect actions above are
              the primary path (as links), so both footer actions are links.
            "I'll manage Apple myself" / "Actually, let Thanx handle it" persist
            the decision (declined / pending) so it sticks across visits. */}
        <div className="d-flex align-items-center justify-content-between flex-wrap mt-l">
          <Button kind={Kind.MINIMAL} className="p-0" onClick={onDismiss}>
            {t(`${copy}.remind_later`)}
          </Button>
          {isGrant ? (
            <div className="d-flex align-items-center">
              <Button
                kind={Kind.SECONDARY}
                onClick={() => {
                  onDeclineAuthority()
                  setView('self_serve')
                }}
                className="mr-xs"
              >
                {t('grant.secondary')}
              </Button>
              <Button kind={Kind.PRIMARY} onClick={onGrantAuthority}>
                {t('grant.primary')}
              </Button>
            </div>
          ) : (
            <Button
              kind={Kind.MINIMAL}
              className="p-0"
              onClick={() => {
                onReconsider()
                setView('grant')
              }}
            >
              {t('self_serve.secondary')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default CredentialSharingNagModal
